import React from 'react';
import { X } from 'lucide-react';

interface ContextModalProps {
    isOpen: boolean;
    isAddingNew: boolean;
    title: string;
    content: string;
    prompt?: string; // Optional prompt to use as placeholder
    onTitleChange: (title: string) => void;
    onContentChange: (content: string) => void;
    onSave: () => void;
    onCancel: () => void;
    isLoading: boolean;
    getCharacterCountColor: (current: number, max: number) => string;
}

const ContextModal: React.FC<ContextModalProps> = ({
    isOpen,
    isAddingNew,
    title,
    content,
    prompt,
    onTitleChange,
    onContentChange,
    onSave,
    onCancel,
    isLoading,
    getCharacterCountColor
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-slate-900">
                        {isAddingNew ? 'Add context' : 'Edit context'}
                    </h2>
                    <button
                        onClick={onCancel}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Title
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => onTitleChange(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="Enter title..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Content
                        </label>
                        <textarea
                            value={content}
                            onChange={(e) => onContentChange(e.target.value)}
                            rows={6}
                            maxLength={50000}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                            placeholder={prompt || "Enter content..."}
                        />
                        <div className="flex items-center justify-between mt-2">
                            <p className={`text-sm ${getCharacterCountColor(content.length, 50000)}`}>
                                {content.length.toLocaleString()}/50,000 characters
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onSave}
                        disabled={isLoading || !title.trim()}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {isLoading ? 'Saving...' : (isAddingNew ? 'Add Context' : 'Save Changes')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContextModal;
